import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, CalendarDays, Calendar as CalIcon, Clock,
  Ban, Plus, X, Loader2, RotateCw, Trash2, MapPin, Users, AlertCircle, CheckCircle2,
} from "lucide-react";
import api from "@/lib/api";

const VIEWS = [
  { key: "month", label: "Month" },
  { key: "week", label: "Week" },
  { key: "day", label: "Day" },
];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseIso = (s) => new Date(`${s}T00:00:00`);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfWeek = (d) => { const x = new Date(d); x.setDate(d.getDate() - d.getDay()); return x; };
const same = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const statusColor = (s) => ({
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-indigo-50 text-indigo-700 border-indigo-200",
  cancelled: "bg-gray-100 text-gray-500 border-gray-200 line-through",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
})[s] || "bg-gray-100 text-gray-700 border-gray-200";

function DayCell({ d, extended, view, cursor, selected, setSelected, bookingsByDay, blockedByDay, maxPerDay }) {
  const dISO = iso(d);
  const inMonth = view !== "month" || d.getMonth() === cursor.getMonth();
  const isSelected = same(d, selected);
  const isToday = same(d, new Date());
  const dayBookings = bookingsByDay[dISO] || [];
  const blk = blockedByDay[dISO];
  const full = dayBookings.length >= maxPerDay;
  return (
    <button
      onClick={() => setSelected(d)}
      data-testid={`day-${dISO}`}
      className={`relative text-left rounded-lg p-2 border transition-all ${
        isSelected ? "border-[#4F46E5] ring-2 ring-[#4F46E5]/20" : "border-gray-200 hover:border-gray-300"
      } ${!inMonth ? "opacity-40" : ""} ${blk ? "bg-rose-50" : "bg-white hover:bg-gray-50"} ${extended ? "min-h-[120px]" : "aspect-square min-h-[90px]"}`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-xs font-semibold ${isToday ? "text-[#4F46E5]" : "text-gray-700"}`}>
          {d.getDate()}
        </span>
        {blk && <Ban className="w-3 h-3 text-rose-500" />}
        {!blk && full && <AlertCircle className="w-3 h-3 text-amber-500" />}
      </div>
      {dayBookings.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {dayBookings.slice(0, 2).map((b) => (
            <div key={b.id} className={`text-[10px] px-1 py-0.5 rounded border truncate ${statusColor(b.status)}`}>
              {b.school_name}
            </div>
          ))}
          {dayBookings.length > 2 && <div className="text-[10px] text-gray-500">+{dayBookings.length - 2} more</div>}
        </div>
      )}
    </button>
  );
}

export default function CalendarView() {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [maxPerDay, setMaxPerDay] = useState(5);
  const [loading, setLoading] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockDate, setBlockDate] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [blockHoliday, setBlockHoliday] = useState(false);
  const [reschedule, setReschedule] = useState(null);      // booking obj
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [cancel, setCancel] = useState(null);              // booking obj
  const [cancelReason, setCancelReason] = useState("");
  const [busy, setBusy] = useState(false);

  const [start, end] = useMemo(() => {
    if (view === "day") return [iso(cursor), iso(cursor)];
    if (view === "week") { const s = startOfWeek(cursor); return [iso(s), iso(addDays(s, 6))]; }
    const s = startOfMonth(cursor);
    const gridStart = addDays(s, -s.getDay());
    return [iso(gridStart), iso(addDays(gridStart, 41))];
  }, [view, cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/bookings/calendar?start=${start}&end=${end}`);
      setBookings(r.data.bookings || []);
      setBlocked(r.data.blocked || []);
      setMaxPerDay(r.data.max_per_day || 5);
    } catch { toast.error("Could not load calendar"); }
    finally { setLoading(false); }
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const bookingsByDay = useMemo(() => {
    const m = {};
    bookings.forEach((b) => { (m[b.preferred_date] = m[b.preferred_date] || []).push(b); });
    return m;
  }, [bookings]);
  const blockedByDay = useMemo(() => Object.fromEntries(blocked.map((b) => [b.date, b])), [blocked]);

  const doBlock = async () => {
    if (!blockDate) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      await api.post("/bookings/blocked-dates", { date: blockDate, reason: blockReason, is_holiday: blockHoliday });
      toast.success("Date blocked");
      setBlockOpen(false); setBlockDate(""); setBlockReason(""); setBlockHoliday(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to block"); }
    finally { setBusy(false); }
  };

  const doUnblock = async (id) => {
    if (!window.confirm("Unblock this date?")) return;
    try { await api.delete(`/bookings/blocked-dates/${id}`); toast.success("Unblocked"); load(); }
    catch { toast.error("Failed to unblock"); }
  };

  const doReschedule = async () => {
    if (!rescheduleDate) { toast.error("Pick a new date"); return; }
    setBusy(true);
    try {
      await api.patch(`/bookings/${reschedule.id}/reschedule`, { date: rescheduleDate, notify: true });
      toast.success("Rescheduled + email sent");
      setReschedule(null); setRescheduleDate("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reschedule failed"); }
    finally { setBusy(false); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.patch(`/bookings/${cancel.id}/cancel`, { reason: cancelReason, notify: true });
      toast.success("Cancelled + email sent");
      setCancel(null); setCancelReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
    finally { setBusy(false); }
  };

  const shift = (dir) => {
    if (view === "day") setCursor(addDays(cursor, dir));
    else if (view === "week") setCursor(addDays(cursor, dir * 7));
    else setCursor(addMonths(cursor, dir));
  };

  const daysGrid = useMemo(() => {
    if (view === "day") return [cursor];
    if (view === "week") { const s = startOfWeek(cursor); return Array.from({ length: 7 }, (_, i) => addDays(s, i)); }
    const s = startOfMonth(cursor);
    const first = addDays(s, -s.getDay());
    return Array.from({ length: 42 }, (_, i) => addDays(first, i));
  }, [view, cursor]);

  const selectedIso = iso(selected);
  const selectedBookings = bookingsByDay[selectedIso] || [];
  const selectedBlock = blockedByDay[selectedIso];
  const curTitle = view === "day"
    ? cursor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const dayCellProps = { view, cursor, selected, setSelected, bookingsByDay, blockedByDay, maxPerDay };


  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} data-testid="calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Booking calendar</h1>
          <p className="text-sm text-gray-500 mt-1">Availability, holidays, and workflow — all in one place.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full bg-gray-100 p-1" data-testid="calendar-views">
            {VIEWS.map((v) => (
              <button
                key={v.key} onClick={() => setView(v.key)}
                data-testid={`view-${v.key}`}
                className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                  view === v.key ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
                }`}
              >{v.label}</button>
            ))}
          </div>
          <button onClick={() => { setBlockDate(iso(selected)); setBlockOpen(true); }} data-testid="calendar-block-btn"
            className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-xs font-medium px-3 py-2 rounded-lg hover:bg-[#1e293b]">
            <Ban className="w-3.5 h-3.5" /> Block date
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Calendar grid */}
        <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <button onClick={() => shift(-1)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-gray-100 text-gray-700" aria-label="Previous" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <div className="font-display text-lg font-semibold tabular-nums" data-testid="cal-title">{curTitle}</div>
            <div className="flex items-center gap-1">
              <button onClick={() => { setCursor(new Date()); setSelected(new Date()); }} data-testid="cal-today" className="text-xs font-medium px-2.5 py-1.5 rounded-md hover:bg-gray-100 text-gray-700">Today</button>
              <button onClick={() => shift(1)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-gray-100 text-gray-700" aria-label="Next" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>

          {view === "month" && (
            <div className="p-3">
              <div className="grid grid-cols-7 gap-1 mb-1">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                  <div key={d} className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 text-center py-1">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1" data-testid="month-grid">
                {daysGrid.map((d) => <DayCell key={iso(d)} d={d} {...dayCellProps} />)}
              </div>
            </div>
          )}

          {view === "week" && (
            <div className="p-3">
              <div className="grid grid-cols-7 gap-2" data-testid="week-grid">
                {daysGrid.map((d) => (
                  <div key={iso(d)} className="space-y-1">
                    <div className="text-[10px] uppercase text-gray-400 font-semibold text-center">
                      {d.toLocaleDateString("en-US", { weekday: "short" })}
                    </div>
                    <DayCell d={d} extended {...dayCellProps} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {view === "day" && (
            <div className="p-4" data-testid="day-view">
              <div className="text-sm font-medium text-gray-500 mb-3">{cursor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
              {selectedBlock ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-3">
                  <Ban className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-rose-900">Date blocked{selectedBlock.is_holiday ? " · Holiday" : ""}</div>
                    <div className="text-xs text-rose-700 mt-1">{selectedBlock.reason || "No reason provided"}</div>
                  </div>
                </div>
              ) : selectedBookings.length === 0 ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />
                  <div className="text-sm text-emerald-800">Available — no bookings scheduled.</div>
                </div>
              ) : (
                <ul className="space-y-2">
                  {selectedBookings.map((b) => (
                    <li key={b.id} className="rounded-xl border border-gray-200 bg-white p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">{b.school_name}</div>
                        <div className="text-xs text-gray-500">{b.teacher_count} teachers · {b.city}</div>
                      </div>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${statusColor(b.status)}`}>{b.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {loading && <div className="border-t border-gray-100 py-2 text-center text-xs text-gray-500"><Loader2 className="w-3 h-3 animate-spin inline mr-1.5" /> Refreshing…</div>}
        </div>

        {/* Right panel — selected day */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="calendar-side-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Selected</div>
              <div className="text-base font-semibold text-gray-900">{selected.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</div>
            </div>
            <CalIcon className="w-5 h-5 text-gray-400" />
          </div>

          {selectedBlock ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="panel-blocked">
              <div className="flex items-center gap-2 mb-1"><Ban className="w-4 h-4 text-rose-600" /><span className="text-sm font-semibold text-rose-900">Blocked</span>{selectedBlock.is_holiday && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white text-rose-700 border border-rose-200 font-semibold">Holiday</span>}</div>
              <div className="text-xs text-rose-700">{selectedBlock.reason || "No reason"}</div>
              <button onClick={() => doUnblock(selectedBlock.id)} data-testid="panel-unblock" className="mt-3 text-xs inline-flex items-center gap-1 text-rose-700 hover:text-rose-900 font-medium">
                <Trash2 className="w-3 h-3" /> Unblock this date
              </button>
            </div>
          ) : (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-3 inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Available</div>
          )}

          {selectedBookings.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-gray-700 mb-2">Bookings · {selectedBookings.length}/{maxPerDay}</div>
              <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {selectedBookings.map((b) => (
                  <li key={b.id} data-testid={`side-booking-${b.id}`} className="rounded-lg border border-gray-200 p-3">
                    <div className="text-sm font-semibold text-gray-900">{b.school_name}</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-0.5"><Users className="w-3 h-3" /> {b.teacher_count}</span>
                      {b.city && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {b.city}</span>}
                      {b.rescheduled_from && <span className="inline-flex items-center gap-0.5 text-amber-700"><Clock className="w-3 h-3" /> moved from {b.rescheduled_from}</span>}
                    </div>
                    <div className="mt-2 flex items-center gap-1.5">
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${statusColor(b.status)}`}>{b.status}</span>
                      <button
                        onClick={() => { setReschedule(b); setRescheduleDate(b.preferred_date); }}
                        data-testid={`reschedule-${b.id}`}
                        disabled={b.status === "cancelled"}
                        className="ml-auto text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40 font-medium"
                      >
                        <RotateCw className="w-3 h-3" /> Reschedule
                      </button>
                      <button
                        onClick={() => setCancel(b)}
                        data-testid={`cancel-${b.id}`}
                        disabled={b.status === "cancelled"}
                        className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-40 font-medium"
                      >
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-gray-100 text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Legend</div>
          <div className="mt-2 space-y-1 text-xs text-gray-600">
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-rose-50 border border-rose-200" /> Blocked / holiday</div>
            <div className="flex items-center gap-2"><AlertCircle className="w-3 h-3 text-amber-500" /> Day at capacity ({maxPerDay})</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200" /> Approved booking</div>
          </div>
        </div>
      </div>

      {/* Block date modal */}
      {blockOpen && createPortal(
        <AnimatePresence>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
            onClick={() => setBlockOpen(false)}>
            <motion.div initial={{ y: 10, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ opacity: 0 }}
              onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-sm max-h-[90vh] overflow-y-auto" data-testid="block-modal">
              <h2 className="font-display text-xl font-semibold">Block a date</h2>
              <p className="text-xs text-gray-500 mt-0.5">No new bookings can be created for this date.</p>
              <div className="mt-4 space-y-2.5">
                <input type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} data-testid="block-date"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none" />
                <input placeholder="Reason (e.g. National holiday)" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} data-testid="block-reason"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none" />
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={blockHoliday} onChange={(e) => setBlockHoliday(e.target.checked)} data-testid="block-holiday" className="w-4 h-4 rounded border-gray-300 text-[#4F46E5] focus:ring-[#4F46E5]" />
                  Mark as holiday
                </label>
                <div className="flex items-center gap-2 justify-end pt-2">
                  <button onClick={() => setBlockOpen(false)} className="text-sm text-gray-600 hover:bg-gray-100 px-3 py-2 rounded-lg">Cancel</button>
                  <button onClick={doBlock} disabled={busy} data-testid="block-submit"
                    className="inline-flex items-center gap-1 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1e293b] disabled:opacity-50">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Block
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Reschedule modal */}
      {reschedule && createPortal(
        <AnimatePresence>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
            onClick={() => setReschedule(null)}>
            <motion.div initial={{ y: 10, scale: 0.98 }} animate={{ y: 0, scale: 1 }}
              onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-sm max-h-[90vh] overflow-y-auto" data-testid="reschedule-modal">
              <div className="flex items-center gap-2 mb-1"><RotateCw className="w-4 h-4 text-[#4F46E5]" /><h2 className="font-display text-xl font-semibold">Reschedule</h2></div>
              <p className="text-xs text-gray-500">Currently on <b>{reschedule.preferred_date}</b> for {reschedule.school_name}.</p>
              <div className="mt-4 space-y-2.5">
                <input type="date" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} data-testid="reschedule-date"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none" />
                <div className="text-[11px] text-gray-500 inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Conflicts and blocked dates will be rejected server-side. Contact will be notified by email.</div>
                <div className="flex items-center gap-2 justify-end pt-2">
                  <button onClick={() => setReschedule(null)} className="text-sm text-gray-600 hover:bg-gray-100 px-3 py-2 rounded-lg">Close</button>
                  <button onClick={doReschedule} disabled={busy || !rescheduleDate} data-testid="reschedule-submit"
                    className="inline-flex items-center gap-1 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1e293b] disabled:opacity-50">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />} Reschedule
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Cancel modal */}
      {cancel && createPortal(
        <AnimatePresence>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
            onClick={() => setCancel(null)}>
            <motion.div initial={{ y: 10, scale: 0.98 }} animate={{ y: 0, scale: 1 }}
              onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-sm max-h-[90vh] overflow-y-auto" data-testid="cancel-modal">
              <div className="flex items-center gap-2 mb-1"><X className="w-4 h-4 text-rose-600" /><h2 className="font-display text-xl font-semibold">Cancel booking</h2></div>
              <p className="text-xs text-gray-500">This will cancel {cancel.school_name} on {cancel.preferred_date}.</p>
              <textarea rows={3} placeholder="Reason (optional, included in email)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-reason"
                className="mt-4 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none resize-none" />
              <div className="flex items-center gap-2 justify-end pt-2 mt-2">
                <button onClick={() => setCancel(null)} className="text-sm text-gray-600 hover:bg-gray-100 px-3 py-2 rounded-lg">Keep booking</button>
                <button onClick={doCancel} disabled={busy} data-testid="cancel-submit"
                  className="inline-flex items-center gap-1 bg-rose-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-rose-700 disabled:opacity-50">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Confirm cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </motion.div>
  );
}